import { tenantApi } from '@shared/api/tenantApi';
import { unwrap, unwrapList } from '@shared/api/unwrap';

export interface AccountAddress {
  account_address_id: string;
  account_id: string;
  label?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  is_primary: boolean;
  is_billing: boolean;
  is_delivery: boolean;
  is_active: boolean;
}

export interface AddressPayload {
  label?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  is_primary?: boolean;
  is_billing?: boolean;
  is_delivery?: boolean;
  is_active?: boolean;
}

export interface AccountPaymentMethod {
  account_payment_method_id: string;
  account_id: string;
  label?: string | null;
  payment_method_type: string;
  card_type?: string | null;
  card_last4?: string | null;
  card_exp_month?: number | null;
  card_exp_year?: number | null;
  card_holder_name?: string | null;
  bank_name?: string | null;
  bank_account_type?: string | null;
  bank_account_last4?: string | null;
  gateway_name?: string | null;
  is_default: boolean;
  is_active: boolean;
}

export interface PaymentChannel {
  payment_channel_id: string;
  name: string;
  code: string;
  provider: string;
  description?: string;
  is_default: boolean;
}

export interface TokenizationLinkResponse {
  payment_link_id?: string;
  url?: string;
  caller_url?: string;
  errorMessage?: string;
}

export interface CompleteTokenizationPayload {
  payment_link_id: string;
  token: string;
  masked_number: string;
  cardholder?: string;
  expiry_month?: string;
  expiry_year?: string;
  gateway_metadata?: Record<string, unknown>;
}

export async function fetchSavedAddresses(): Promise<AccountAddress[]> {
  const { data } = await tenantApi.get('/public/me/addresses');
  return unwrapList<AccountAddress>(data);
}

export async function createSavedAddress(payload: AddressPayload): Promise<AccountAddress> {
  const { data } = await tenantApi.post('/public/me/addresses', payload);
  return unwrap<AccountAddress>(data);
}

export async function updateSavedAddress(
  id: string,
  payload: AddressPayload,
): Promise<AccountAddress> {
  const { data } = await tenantApi.put(`/public/me/addresses/${encodeURIComponent(id)}`, payload);
  return unwrap<AccountAddress>(data);
}

export async function deleteSavedAddress(id: string): Promise<void> {
  await tenantApi.delete(`/public/me/addresses/${encodeURIComponent(id)}`);
}

export async function fetchAccountPaymentMethods(): Promise<AccountPaymentMethod[]> {
  const { data } = await tenantApi.get('/public/me/payment-methods');
  return unwrapList<AccountPaymentMethod>(data);
}

export async function deleteAccountPaymentMethod(id: string): Promise<void> {
  await tenantApi.delete(`/public/me/payment-methods/${encodeURIComponent(id)}`);
}

export async function fetchPaymentChannels(): Promise<PaymentChannel[]> {
  const { data } = await tenantApi.get('/public/payment-channels');
  return unwrapList<PaymentChannel>(data);
}

export async function requestTokenizationLink(payload: {
  payment_channel_id?: string;
  return_url: string;
}): Promise<TokenizationLinkResponse> {
  const { data } = await tenantApi.post('/public/me/payment-methods/tokenization-link', payload);
  return unwrap<TokenizationLinkResponse>(data);
}

export async function completeTokenization(
  payload: CompleteTokenizationPayload,
): Promise<AccountPaymentMethod> {
  const { data } = await tenantApi.post('/public/me/payment-methods/tokenization-complete', payload);
  return unwrap<AccountPaymentMethod>(data);
}
